import React from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Security as SecurityIcon,
  Description as DescriptionIcon,
  Groups as GroupsIcon,
  Assessment as AssessmentIcon,
  Insights as InsightsIcon,
  BugReport as BugReportIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import PageLayout from './PageLayout';
import { colors, typography, cardStyles } from '../styles/theme';
import { isContractReviewEnabled, isIrmtEnabled, isIrisEnabled, isBaselineSecurityEnabled, isThreatHuntEnabled } from '../utils/features';
import { useMe } from '../hooks/useMe';
import { canAccessContractReview, canAccessQuestionnaire, canAccessRiskManagement, canAccessBaselineSecurity, canAccessSecurityIncident, canAccessThreatHunt } from '../permissions/capabilities';

type ModuleVariant = 'accent' | 'muted';

interface ModuleDef {
  id: string;
  title: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  variant: ModuleVariant;
}

const ICON_BOX = 56;

function iconTile(variant: ModuleVariant, children: React.ReactNode) {
  const isAccent = variant === 'accent';
  return (
    <Box
      sx={{
        width: ICON_BOX,
        height: ICON_BOX,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        backgroundColor: isAccent ? 'rgba(72, 161, 173, 0.14)' : 'rgba(0, 0, 0, 0.06)',
        color: isAccent ? colors.primaryLight : colors.text.secondary,
        '& .MuiSvgIcon-root': {
          fontSize: 28,
        },
      }}
    >
      {children}
    </Box>
  );
}

interface ModuleCardProps {
  module: ModuleDef;
  onNavigate: (path: string) => void;
}

const ModuleCard: React.FC<ModuleCardProps> = ({ module, onNavigate }) => {
  const isAccent = module.variant === 'accent';
  const linkColor = isAccent ? colors.primaryLight : colors.text.secondary;

  return (
    <Card
      elevation={0}
      sx={{
        ...cardStyles,
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        border: `1px solid ${colors.border.light}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)',
        },
      }}
      onClick={() => onNavigate(module.path)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNavigate(module.path);
        }
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          pt: 3,
          pb: 2.5,
          px: 2.5,
          '&:last-child': { pb: 2.5 },
        }}
      >
        {iconTile(module.variant, module.icon)}
        <Typography
          component="h2"
          sx={{
            mt: 2,
            mb: 1,
            fontWeight: typography.fontWeight.bold,
            fontSize: typography.fontSize.lg,
            color: colors.text.primary,
            lineHeight: typography.lineHeight.tight,
          }}
        >
          {module.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: colors.text.secondary,
            lineHeight: typography.lineHeight.relaxed,
            flex: 1,
            mb: 2,
          }}
        >
          {module.description}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.semibold,
            color: linkColor,
            mt: 'auto',
          }}
        >
          Open module →
        </Typography>
      </CardContent>
    </Card>
  );
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { me } = useMe();

  const modules: ModuleDef[] = [
    ...(canAccessQuestionnaire(me)
      ? [
          {
            id: 'questionnaire',
            title: 'Security Assessment',
            description:
              'Manage client security questionnaires and review automated AI answers',
            path: '/questionnaire',
            icon: <SecurityIcon aria-hidden />,
            variant: 'accent' as const,
          },
        ]
      : []),
    ...(isContractReviewEnabled() && canAccessContractReview(me)
      ? [
          {
            id: 'contract-review',
            title: 'Contract Review',
            description: 'AI-powered security clause extraction and compliance assessment',
            path: '/contract-review',
            icon: <DescriptionIcon aria-hidden />,
            variant: 'accent' as const,
          },
        ]
      : []),
    ...(isIrmtEnabled() && canAccessRiskManagement(me)
      ? [
          {
            id: 'risk-management',
            title: 'Risk Management',
            description:
              'Track, assess and govern organisational risks with AI-assisted analysis and committee workflows',
            path: '/risk-management',
            icon: <AssessmentIcon aria-hidden />,
            variant: 'accent' as const,
          },
        ]
      : []),
    ...(isIrisEnabled() && canAccessSecurityIncident(me)
      ? [
          {
            id: 'security-incident',
            title: 'Security Incident',
            description:
              'Manage security incidents and track their status',
            path: '/security-incident',
            icon: <InsightsIcon aria-hidden />,
            variant: 'accent' as const,
          },
        ]
      : []),
    ...(isThreatHuntEnabled() && canAccessThreatHunt(me)
      ? [
          {
            id: 'threat-hunt',
            title: 'Threat Hunt',
            description:
              'Hunt for threats and investigate suspicious activity across the organisation',
            path: '/threat-hunt',
            icon: <BugReportIcon aria-hidden />,
            variant: 'accent' as const,
          },
        ]
      : []),
    ...(isBaselineSecurityEnabled() && canAccessBaselineSecurity(me)
      ? [
          {
            id: 'baseline-security',
            title: 'Baseline Security',
            description:
              'Monitor and manage InfoSec baseline compliance across accounts, tracking Security Champions, training completion and exposure levels',
            path: '/baseline-security',
            icon: <ShieldIcon aria-hidden />,
            variant: 'accent' as const,
          },
        ]
      : []),
    ...(me?.can_manage_roles || me?.can_manage_users
      ? [
          {
            id: 'admin',
            title: 'Administration',
            description: 'Manage users, configure settings',
            path: me?.can_manage_roles ? '/admin/roles' : '/admin/users',
            icon: <GroupsIcon aria-hidden />,
            variant: 'muted' as const,
          },
        ]
      : []),
  ];

  const mdColumns =
    modules.length >= 3
      ? 'repeat(3, minmax(0, 1fr))'
      : modules.length === 2
        ? 'repeat(2, minmax(0, 1fr))'
        : 'minmax(0, 360px)';

  return (
    <PageLayout>
      <Box sx={{ maxWidth: 960, mx: 'auto', mb: 4, textAlign: 'center' }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: typography.fontFamily.heading,
            fontWeight: typography.fontWeight.bold,
            fontSize: typography.fontSize['3xl'],
            color: colors.text.primary,
            lineHeight: typography.lineHeight.tight,
          }}
        >
          Welcome to OneSec Platform
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: mdColumns },
          gap: 3,
          maxWidth: 960,
          mx: 'auto',
          alignItems: 'stretch',
          justifyContent: modules.length === 1 ? 'center' : undefined,
        }}
      >
        {modules.map((mod) => (
          <ModuleCard key={mod.id} module={mod} onNavigate={navigate} />
        ))}
      </Box>
    </PageLayout>
  );
};

export default HomePage;
